import { useEffect, useState } from 'react';
import { readCache, writeCache } from '../../lib/swr';

/**
 * Run an async loader on mount / when `deps` change.
 *
 * Pass `cacheKey` to get stale-while-revalidate behaviour: the last good result
 * for that key renders instantly (no spinner), the loader still runs in the
 * background, and fresh data swaps in when it arrives. A background refresh that
 * fails keeps the cached data on screen instead of showing an error.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = [], cacheKey?: string) {
  const [data, setData] = useState<T | null>(() =>
    (cacheKey ? readCache<T>(cacheKey)?.data ?? null : null));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(() => !(cacheKey && readCache<T>(cacheKey)));
  const [refreshing, setRefreshing] = useState(false);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let alive = true;
    const cached = cacheKey ? readCache<T>(cacheKey) : null;
    if (cached) { setData(cached.data); setLoading(false); setRefreshing(true); }
    else { setLoading(true); }
    setError('');
    fn().then(
      (d) => {
        if (!alive) return;
        setData(d);
        if (cacheKey) writeCache(cacheKey, d);
        setLoading(false); setRefreshing(false);
      },
      (e) => {
        if (!alive) return;
        if (!cached) setError(e instanceof Error ? e.message : String(e));
        setLoading(false); setRefreshing(false);
      },
    );
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, cacheKey]);
  return { data, error, loading, refreshing, reload: () => setNonce((n) => n + 1) };
}

type Row = Record<string, unknown>;

/** Generic read-only table from an array of row objects. */
export function Table({ rows, columns, empty }: { rows: Row[]; columns?: string[]; empty?: string }) {
  if (!rows.length) return <p className="muted">{empty ?? 'No rows.'}</p>;
  const cols = columns ?? Object.keys(rows[0]);
  return (
    <div className="tableScroll">
      <table className="grid">
        <thead>
          <tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{cols.map((c) => <td key={c}>{fmtCell(r[c])}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtCell(v: unknown): string {
  if (v === true) return 'Yes';
  if (v === false) return '';
  if (v === null || v === undefined) return '';
  return String(v);
}

export function Banner({ error, ok }: { error?: string; ok?: string }) {
  if (error) return <p className="error">{error}</p>;
  if (ok) return <p style={{ color: 'var(--ok)' }}>{ok}</p>;
  return null;
}

export function Empty({ icon = '📭', children }: { icon?: string; children: React.ReactNode }) {
  return <div className="empty"><div className="ic">{icon}</div>{children}</div>;
}

/** A card in an admin card-list. `stripe` colours the left edge by status. */
export function Item(props: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  stripe?: 'ok' | 'warn' | 'bad';
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="item">
      <div className={`stripe ${props.stripe ?? ''}`} />
      <div className="body">
        <div className="title">{props.title}</div>
        {props.meta && <div className="meta">{props.meta}</div>}
        {props.children}
      </div>
      {props.actions && <div className="acts">{props.actions}</div>}
    </div>
  );
}

export function Advanced({ children }: { children: React.ReactNode }) {
  return (
    <details className="disclosure">
      <summary>Advanced options</summary>
      {children}
    </details>
  );
}
